import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NodeData } from '../../types';
import { 
  Image as ImageIcon, 
  Upload, 
  Loader2, 
  Check, 
  AlertCircle,
  X,
  ExternalLink,
  Settings,
  Lock,
  Unlock,
  Edit2,
  Calendar,
  Clock,
  Twitter
} from 'lucide-react';
import { useFileUrl } from '../../hooks/useFileUrl';
import { isLegacyMediaRef } from '../../utils/mediaStorage';
import { 
  isTwitterConnected, 
  getTwitterUsername, 
  getTwitterCharLimit,
  getTwitterUserTier,
  postToTwitter,
  prepareMediaForTwitter,
  getTwitterTokens
} from '../../utils/twitter';
import { logger } from '../../utils/logger';

interface ShareNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  userId?: string;
  connectedImageUrl?: string; // URL from connected node
  onRequestTwitterAuth?: () => void; // Callback to trigger auth overlay
  isViewOnly?: boolean;
  ownerUserId?: string;
}

type PostStatus = 'idle' | 'posting' | 'success' | 'error' | 'scheduled';

export const ShareNode: React.FC<ShareNodeProps> = ({
  data,
  onDataChange,
  userId,
  connectedImageUrl,
  onRequestTwitterAuth,
  isViewOnly = false,
  ownerUserId,
}) => {
  const [caption, setCaption] = useState(data.shareData?.caption || '');
  const [postStatus, setPostStatus] = useState<PostStatus>(data.shareData?.postStatus || 'idle');
  const [postError, setPostError] = useState<string | null>(data.shareData?.postError || null);
  const [tweetUrl, setTweetUrl] = useState<string | null>(data.shareData?.tweetUrl || null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState<string | undefined>(data.imageUrl);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Lock state
  const isLocked = data.shareData?.isLocked || false;
  
  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitleValue, setEditingTitleValue] = useState(data.title || 'New Post');
  
  // Scheduling state
  const [showScheduleMode, setShowScheduleMode] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<string>(() => {
    // Default to 1 hour from now
    const defaultTime = new Date(Date.now() + 60 * 60 * 1000);
    return defaultTime.toISOString().slice(0, 16); // Format for datetime-local input
  });
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  
  // Restore scheduled state from data
  const scheduledPostId = data.shareData?.scheduledPostId;
  const scheduledAt = data.shareData?.scheduledAt;
  const scheduledStatus = data.shareData?.scheduledStatus;

  // Log mount with auth state
  useEffect(() => {
    logger.debug('ShareNode', `Mounted | userId: ${userId || 'none'} | hasImage: ${!!imageToUse}`);
  }, [userId]);

  // Toggle lock state
  const toggleLock = useCallback(() => {
    if (isViewOnly) return;
    onDataChange({
      ...data,
      shareData: {
        ...data.shareData,
        caption: caption,
        platform: 'twitter',
        isLocked: !isLocked,
      },
    });
  }, [data, caption, isLocked, onDataChange]);

  // Handle title save
  const handleTitleSave = useCallback(() => {
    const newTitle = editingTitleValue.trim() || 'New Post';
    onDataChange({
      ...data,
      title: newTitle,
    });
    setIsEditingTitle(false);
  }, [data, editingTitleValue, onDataChange]);

  // Handle title key press (Enter to save, Escape to cancel)
  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setEditingTitleValue(data.title || 'New Post');
      setIsEditingTitle(false);
    }
  }, [handleTitleSave, data.title]);

  // Determine which image to use: connected > local > data
  const imageToUse = connectedImageUrl || localImageUrl || data.imageUrl;
  
  // Resolve file:// reference to actual URL
  const { url: resolvedImageUrl, isLoading: isResolvingUrl } = useFileUrl(userId, imageToUse, undefined, ownerUserId);

  // Handle image load to get dimensions
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    
    // Store dimensions in data if they changed
    if (width && height && (width !== data.originalWidth || height !== data.originalHeight)) {
      onDataChange({
        ...data,
        originalWidth: width,
        originalHeight: height,
      });
    }
    setImageLoaded(true);
  }, [data, onDataChange]);

  // Reset imageLoaded when image URL changes
  useEffect(() => {
    setImageLoaded(false);
  }, [imageToUse]);

  // Twitter connection state
  const [isConnected, setIsConnected] = useState(isTwitterConnected());
  const [username, setUsername] = useState<string | null>(getTwitterUsername());
  const [charLimit, setCharLimit] = useState(getTwitterCharLimit());
  const [userTier, setUserTier] = useState(getTwitterUserTier());

  // Refresh Twitter connection state periodically
  useEffect(() => {
    const refreshConnectionState = () => {
      setIsConnected(isTwitterConnected());
      setUsername(getTwitterUsername());
      setCharLimit(getTwitterCharLimit());
      setUserTier(getTwitterUserTier());
    };

    refreshConnectionState();
    const interval = setInterval(refreshConnectionState, 1000);
    return () => clearInterval(interval);
  }, []);

  // A caption changed elsewhere (another person, or Claude through the connector) shows here. While
  // the person is in the field it waits, and comes in when they leave it without having typed.
  const storedCaption = data.shareData?.caption || '';
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const typedRef = useRef(false);
  useEffect(() => {
    if (document.activeElement !== captionRef.current) setCaption(storedCaption);
  }, [storedCaption]);

  // Sync caption to data
  useEffect(() => {
    if (caption !== data.shareData?.caption) {
      onDataChange({
        ...data,
        shareData: {
          ...data.shareData,
          caption,
          platform: 'twitter',
        },
      });
    }
  }, [caption]);

  // Handle file upload
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  }, []);

  const processFile = (file: File) => {
    if (isViewOnly) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setLocalImageUrl(result);
      onDataChange({
        ...data,
        imageUrl: result,
      });
    };
    reader.readAsDataURL(file);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent canvas from handling this
    if (isLocked) return; // Don't accept drops when locked
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingOver(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent canvas from creating a new node
    setIsDraggingOver(false);
    if (isViewOnly) return; // Don't process drops when view only
    if (isLocked) return; // Don't process drops when locked
    
    // Check for storage file drop (from Storage Browser)
    const jsonData = e.dataTransfer.getData('application/json');
    if (jsonData) {
      try {
        const parsed = JSON.parse(jsonData);
        if (parsed.type === 'storage-file' && parsed.file) {
          const { url, fileId, type } = parsed.file;
          // Only accept image files from storage
          if (type === 'image' && url) {
            // If it's a file:// reference, store it, the useFileUrl hook will resolve it
            // If it's a direct URL, use it directly
            setLocalImageUrl(url);
            onDataChange({
              ...data,
              imageUrl: url,
              // Store fileId for potential reference
              ...(fileId && { storageFileId: fileId }),
            });
            return;
          }
        }
      } catch (err) {
        // Not valid JSON, continue to check for regular file drop
      }
    }
    
    // Check for regular file drop
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const clearImage = () => {
    if (isViewOnly) return;
    setLocalImageUrl(undefined);
    onDataChange({ ...data, imageUrl: undefined });
  };

  // Post to Twitter
  const handlePost = async () => {
    if (isViewOnly) return;
    if (!isConnected) {
      onRequestTwitterAuth?.();
      return;
    }

    if (caption.length > charLimit) {
      setPostError(`Caption exceeds ${charLimit} character limit`);
      setPostStatus('error');
      return;
    }

    // Need either caption or media
    const displayUrl = resolvedImageUrl || imageToUse;
    const hasMedia = displayUrl && !displayUrl.startsWith('file://');
    const hasCaption = caption.trim().length > 0;

    if (!hasMedia && !hasCaption) {
      setPostError('Please add a caption or an image to post');
      setPostStatus('error');
      return;
    }

    setPostStatus('posting');
    setPostError(null);

    try {
      let mediaBase64: string | undefined;
      let mediaType: string | undefined;

      if (hasMedia) {
        const prepared = await prepareMediaForTwitter(displayUrl);
        if (prepared) {
          mediaBase64 = prepared.base64;
          mediaType = prepared.mimeType;
        }
      }

      // postToTwitter expects an options object
      const result = await postToTwitter({
        text: hasCaption ? caption : undefined,
        mediaBase64,
        mediaType,
      });

      if (result.success) {
        setPostStatus('success');
        setTweetUrl(result.tweetUrl || null);
        onDataChange({
          ...data,
          shareData: {
            ...data.shareData,
            caption,
            platform: 'twitter',
            postStatus: 'success',
            tweetId: result.tweetId,
            tweetUrl: result.tweetUrl,
            lastPostedAt: Date.now(),
          },
        });
      } else {
        setPostStatus('error');
        setPostError(result.error || 'Failed to post');
        onDataChange({
          ...data,
          shareData: {
            ...data.shareData,
            caption,
            platform: 'twitter',
            postStatus: 'error',
            postError: result.error,
          },
        });
      }
    } catch (err: any) {
      setPostStatus('error');
      setPostError(err.message || 'Failed to post');
    }
  };

  const resetPost = () => {
    setPostStatus('idle');
    setPostError(null);
    setTweetUrl(null);
    setShowScheduleMode(false);
    setScheduleError(null);
    onDataChange({
      ...data,
      shareData: {
        platform: 'twitter',
        ...data.shareData,
        postStatus: 'idle',
        postError: undefined,
        tweetId: undefined,
        tweetUrl: undefined,
        scheduledPostId: undefined,
        scheduledAt: undefined,
        scheduledStatus: undefined,
      },
    });
  };

  // Schedule post for later
  const handleSchedule = async () => {
    if (isViewOnly) return;
    if (!isConnected) {
      onRequestTwitterAuth?.();
      return;
    }

    if (caption.length > charLimit) {
      setScheduleError(`Caption exceeds ${charLimit} character limit`);
      return;
    }

    // Validate scheduled time
    const scheduledTime = new Date(scheduledDateTime).getTime();
    if (scheduledTime < Date.now() + 60000) {
      setScheduleError('Scheduled time must be at least 1 minute in the future');
      return;
    }

    setIsScheduling(true);
    setScheduleError(null);

    try {
      // Get current tokens (OAuth 1.0a)
      const tokens = getTwitterTokens();
      if (!tokens?.accessToken || !tokens?.accessTokenSecret) {
        throw new Error('Twitter not connected. Please reconnect.');
      }

      // Prepare media if present
      let mediaBase64: string | undefined;
      let mediaType: string | undefined;
      const displayUrl = resolvedImageUrl || imageToUse;
      const hasMedia = displayUrl && !displayUrl.startsWith('file://');

      if (hasMedia) {
        const prepared = await prepareMediaForTwitter(displayUrl);
        if (prepared) {
          mediaBase64 = prepared.base64;
          mediaType = prepared.mimeType;
        }
      }

      // Call schedule API
      const response = await fetch('/.netlify/functions/schedule-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          userId: userId || 'anonymous',
          caption: caption.trim(),
          mediaBase64,
          mediaType,
          scheduledAt: scheduledTime,
          accessToken: tokens.accessToken,
          accessTokenSecret: tokens.accessTokenSecret,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to schedule post');
      }

      // Update state with scheduled post info
      setPostStatus('scheduled');
      setShowScheduleMode(false);
      onDataChange({
        ...data,
        shareData: {
          ...data.shareData,
          caption,
          platform: 'twitter',
          postStatus: 'scheduled',
          scheduledPostId: result.postId,
          scheduledAt: scheduledTime,
          scheduledStatus: 'pending',
        },
      });

    } catch (err: any) {
      setScheduleError(err.message || 'Failed to schedule post');
    } finally {
      setIsScheduling(false);
    }
  };

  // Cancel a scheduled post
  const handleCancelSchedule = async () => {
    if (!scheduledPostId) return;

    setIsScheduling(true);

    try {
      const response = await fetch('/.netlify/functions/schedule-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'cancel',
          userId: userId || 'anonymous',
          postId: scheduledPostId,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to cancel scheduled post');
      }

      // Reset to idle state
      resetPost();

    } catch (err: any) {
      setScheduleError(err.message || 'Failed to cancel scheduled post');
    } finally {
      setIsScheduling(false);
    }
  };

  // Check scheduled post status
  const checkScheduledStatus = async () => {
    if (!scheduledPostId || !userId) return;

    try {
      const response = await fetch('/.netlify/functions/schedule-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get',
          userId: userId || 'anonymous',
          postId: scheduledPostId,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success && result.post) {
        const post = result.post;
        
        // Update status if changed
        if (post.status !== scheduledStatus) {
          onDataChange({
            ...data,
            shareData: {
              platform: 'twitter',
              ...data.shareData,
              scheduledStatus: post.status,
              ...(post.status === 'posted' && {
                postStatus: 'success',
                tweetId: post.tweetId,
                tweetUrl: post.tweetUrl,
                lastPostedAt: post.postedAt ? new Date(post.postedAt).getTime() : Date.now(),
              }),
              ...(post.status === 'failed' && {
                postStatus: 'error',
                postError: post.error,
              }),
            },
          });

          if (post.status === 'posted') {
            setPostStatus('success');
            setTweetUrl(post.tweetUrl || null);
          } else if (post.status === 'failed') {
            setPostStatus('error');
            setPostError(post.error || 'Scheduled post failed');
          }
        }
      }
    } catch (err) {
      console.error('Failed to check scheduled status:', err);
    }
  };

  // Periodically check scheduled post status
  useEffect(() => {
    if (scheduledPostId && scheduledStatus === 'pending') {
      const interval = setInterval(checkScheduledStatus, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [scheduledPostId, scheduledStatus]);

  // Format scheduled time for display
  const formatScheduledTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const displayUrl = resolvedImageUrl || imageToUse;
  const canDisplayImage = displayUrl && !displayUrl.startsWith('file://') && !isLegacyMediaRef(displayUrl);
  const isOverLimit = caption.length > charLimit;

  // Calculate dynamic preview height based on image aspect ratio
  // Use a larger portion of the node for image preview when we have an image
  const hasImage = canDisplayImage && imageLoaded;
  const previewMinHeight = hasImage ? 'min-h-[180px]' : 'min-h-[100px]';

  return (
    <div className={`w-full h-full flex flex-col bg-white ${isLocked ? 'opacity-90' : ''}`}>
      {/* Toolbar with Lock and Rename: an action is the system's quiet text button, a state
          that is on or off is a chip that is pressed, as the switches of the Image and Video nodes */}
      <div className="flex items-center justify-between gap-2 px-3 py-1 border-b border-cream-300 shrink-0 bg-cream-50">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isEditingTitle ? (
            <input
              type="text"
              value={editingTitleValue}
              onChange={(e) => setEditingTitleValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyDown}
              className="ns-field ns-field-sm flex-1 min-w-0"
              autoFocus
              placeholder="Enter name..."
            />
          ) : (
            // (-ml-2 px-2: the icon stands on the line of the caption field below)
            <button
              className="ns-btn ns-btn-xs ns-btn-quiet -ml-2 px-2"
              onClick={() => {
                if (isLocked) return;
                // The name as it is now, which may have been changed elsewhere since this node opened
                setEditingTitleValue(data.title || 'New Post');
                setIsEditingTitle(true);
              }}
              disabled={isLocked}
              title={isLocked ? 'Unlock to rename' : 'Click to rename'}
            >
              <Edit2 size={14} />
              <span>Rename</span>
            </button>
          )}
        </div>
        {/* Locked is a state to mind: pressed, the chip takes the warn tint */}
        <button
          onClick={toggleLock}
          className="ns-chip ns-chip-btn shrink-0"
          data-tone="warn"
          aria-pressed={isLocked}
          title={isLocked ? 'Unlock node' : 'Lock node'}
        >
          {isLocked ? <Lock size={14} /> : <Unlock size={14} />}
          <span>{isLocked ? 'Locked' : 'Lock'}</span>
        </button>
      </div>

      {/* Media Preview Area - flexible height based on image */}
      <div
        className={`relative flex-1 ${previewMinHeight} max-h-[60%] border-b border-cream-300 overflow-hidden transition-colors ${
          isDraggingOver ? 'bg-lavender-soft' : 'bg-cream-50'
        }`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {isResolvingUrl ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 size={24} className="text-ink-faint animate-spin" />
          </div>
        ) : canDisplayImage ? (
          <>
            <img
              src={displayUrl}
              alt="Preview"
              className="w-full h-full object-contain"
              draggable={false}
              onLoad={handleImageLoad}
            />
            {/* Source indicator: lavender, as everything linked */}
            {connectedImageUrl && (
              <div className="ns-chip ns-chip-sm ns-chip-lavender absolute top-2 left-2 shadow-hair">
                Connected
              </div>
            )}
            {/* Clear button (only for local images, disabled when locked) */}
            {!connectedImageUrl && localImageUrl && !isLocked && (
              <button
                onClick={clearImage}
                className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-dark absolute top-2 right-2"
                aria-label="Remove image"
                title="Remove image"
              >
                <X />
              </button>
            )}
          </>
        ) : (
          // Nothing to post yet: the system's empty state. The mark is the 40px one, not the
          // 64px one, because this area can be as low as 100px and nothing may be cut off.
          <div className="ns-empty h-full py-2">
            {isLocked ? (
              <>
                <span className="ns-mark"><Lock /></span>
                <p className="ns-empty-text">Node is locked</p>
              </>
            ) : (
              <>
                {/* The mark is also the button that picks a file */}
                <div className="relative group flex mb-1.5">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    aria-label="Choose an image or a video"
                    title="Choose an image or a video"
                  />
                  <span className="ns-mark group-hover:bg-lavender-soft group-hover:text-lavender-deep transition-colors">
                    <Upload />
                  </span>
                </div>
                <p className="ns-empty-text">
                  {connectedImageUrl === undefined ? 'Drop image or connect a node' : 'No image connected'}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Caption Input */}
      <div className="flex-1 flex flex-col p-3 gap-2 min-h-0">
        <div className="flex-1 relative min-h-0">
          <textarea
            ref={captionRef}
            value={caption}
            onFocus={() => { typedRef.current = false; }}
            onChange={(e) => { typedRef.current = true; setCaption(e.target.value); }}
            onBlur={() => { if (!typedRef.current) setCaption(storedCaption); }}
            placeholder={isLocked ? 'Caption is locked' : 'Write your caption...'}
            // (The ! is needed: the field's own hover and focus rules set the border colour and
            // outweigh a utility. The system has no look for a field that is invalid yet.)
            className={`ns-field h-full ${
              isOverLimit && !isLocked ? '!border-danger focus:ring focus:ring-danger-soft' : ''
            }`}
            aria-invalid={isOverLimit && !isLocked ? true : undefined}
            disabled={postStatus === 'posting' || isLocked}
            readOnly={isLocked}
          />
          {isLocked && (
            <div className="absolute top-2 right-2 text-ink-faint">
              <Lock size={12} />
            </div>
          )}
        </div>

        {/* Character Count */}
        <div className="ns-t-meta flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1.5">
            {userTier === 'premium' && (
              <span className="ns-badge ns-badge-tone">
                Premium
              </span>
            )}
            <span>
              {charLimit.toLocaleString()} char limit
            </span>
          </div>
          {/* text-red-500 is the danger red; the unit test looks for this class */}
          <span className={isOverLimit ? 'text-red-500 font-semibold' : undefined}>
            {caption.length} / {charLimit.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Footer with Actions */}
      <div className="border-t border-cream-300 p-3 shrink-0 bg-cream-50">
        {/* Connection Status */}
        {!isConnected && postStatus === 'idle' && (
          <button
            onClick={onRequestTwitterAuth}
            className="ns-btn ns-btn-xs ns-btn-primary w-full"
          >
            <Twitter size={12} />
            Connect Twitter
          </button>
        )}

        {/* Connected - Ready to Post */}
        {isConnected && postStatus === 'idle' && !showScheduleMode && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-ink-soft">
              <span className="flex items-center gap-1.5">
                <span className="ns-tone-dot" data-tone="ok" />
                @{username}
              </span>
              <button
                onClick={onRequestTwitterAuth}
                className="ns-icon-btn ns-icon-btn-xs"
                title="Twitter settings"
              >
                <Settings />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePost}
                disabled={(!caption.trim() && !canDisplayImage) || isOverLimit}
                className="ns-btn ns-btn-xs ns-btn-primary flex-1"
              >
                <Twitter size={12} />
                Post now
              </button>
              <button
                onClick={() => setShowScheduleMode(true)}
                disabled={(!caption.trim() && !canDisplayImage) || isOverLimit}
                className="ns-icon-btn ns-icon-btn-soft"
                title="Schedule for later"
              >
                <Clock />
              </button>
            </div>
          </div>
        )}

        {/* Schedule Mode */}
        {isConnected && postStatus === 'idle' && showScheduleMode && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="ns-t-label flex items-center gap-1">
                <Calendar size={12} />
                Schedule post
              </span>
              <button
                onClick={() => {
                  setShowScheduleMode(false);
                  setScheduleError(null);
                }}
                className="ns-icon-btn ns-icon-btn-xs"
                aria-label="Close scheduling"
                title="Close scheduling"
              >
                <X />
              </button>
            </div>
            <input
              type="datetime-local"
              value={scheduledDateTime}
              onChange={(e) => setScheduledDateTime(e.target.value)}
              min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
              className="ns-field ns-field-sm"
            />
            {scheduleError && (
              <p className="ns-note" data-tone="danger">{scheduleError}</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowScheduleMode(false);
                  setScheduleError(null);
                }}
                className="ns-btn ns-btn-xs ns-btn-soft flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleSchedule}
                disabled={isScheduling}
                className="ns-btn ns-btn-xs ns-btn-primary flex-1"
              >
                {isScheduling ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Clock size={12} />
                )}
                Schedule
              </button>
            </div>
          </div>
        )}

        {/* Scheduled State */}
        {postStatus === 'scheduled' && scheduledStatus === 'pending' && (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2 py-1 text-[color:var(--tone-deep)]">
              <Clock size={16} />
              <span className="text-[13px] font-semibold">Scheduled</span>
            </div>
            {scheduledAt && (
              <p className="ns-t-small text-center">
                Will post on {formatScheduledTime(scheduledAt)}
              </p>
            )}
            {/* Quiet until it is pointed at, then the danger tint: it takes the post off the schedule */}
            <button
              onClick={handleCancelSchedule}
              disabled={isScheduling}
              className="ns-btn ns-btn-xs ns-btn-quiet w-full"
              data-danger="true"
            >
              {isScheduling ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <X size={12} />
              )}
              Cancel schedule
            </button>
          </div>
        )}

        {/* Posting State */}
        {postStatus === 'posting' && (
          <div className="flex items-center justify-center gap-2 py-2 text-ink-soft">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-[13px] font-semibold">Posting...</span>
          </div>
        )}

        {/* Success State */}
        {postStatus === 'success' && (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2 py-1 text-ok-deep">
              <Check size={16} />
              <span className="text-[13px] font-semibold">Posted!</span>
            </div>
            <div className="flex gap-2">
              {tweetUrl && (
                <a
                  href={tweetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ns-btn ns-btn-xs ns-btn-soft flex-1"
                >
                  <ExternalLink size={12} />
                  View tweet
                </a>
              )}
              <button
                onClick={resetPost}
                className="ns-btn ns-btn-xs ns-btn-primary flex-1"
              >
                Post again
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {postStatus === 'error' && (
          <div className="space-y-2">
            <div className="ns-note" data-tone="danger">
              <AlertCircle />
              <span className="line-clamp-2">{postError || 'Failed to post'}</span>
            </div>
            <button
              onClick={resetPost}
              className="ns-btn ns-btn-xs ns-btn-soft w-full"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
